import {
  objectToQueryString,
  setQueryStringParam,
  getQueryStringParams,
  deleteQueryStringParam,
  snapshotSquadsFromCache,
} from "../../shared/utils";
import { escapeCsvValue, sum } from "../../lib/utils";
import tippy from "tippy.js";
import colors from "tailwindcss/colors";
import { getChartColor } from "../../components/ui/chart-colors";
import { teamTreeExpansionMixin } from "../mixins/teamTreeExpansion";
import { peopleFiltersMixin } from "../mixins/peopleFilters";

// Use smart color selection for consistency with other charts
const _SS_ALLOC_COLOR_OTHER = colors.gray[400]; // Gray 400

export function snapshotAllocationResult(args) {
  const {
    questionId,
    isCoreQuestion,
    summaryEndpoint,
    breakdownEndpoint,
    questionItemOverrides,
    tagOptions,
    snapshotId,
  } = args;

  return {
    ...teamTreeExpansionMixin(),
    ...peopleFiltersMixin(),

    // Configure ID mapping
    rowIdKey: "snapshot_squad_id",

    isCoreQuestion,
    summaryData: {},
    chartData: [],
    comparisonChartData: [],
    colors: {}, // Shared b/w charts and breakdown
    summaryLoaded: false,
    summaryUpdating: false,
    breakdownData: [],
    breakdownOpen: getQueryStringParams().get("question") == questionId,
    breakdownLoaded: false,
    breakdownUpdating: false,
    tableSort: { col: "group_name", dir: "asc" },
    squadNamesFromSquadIds: [],
    init() {
      this.fetchSummary();
      if (this.breakdownOpen) this.openBreakdown();

      this.$watch(
        "$store.snapshotResultFilters.compareTo, $store.snapshotResultFilters.previousSnapshotId",
        () => {
          if (this.compareTo == "vs_prev" || this.compareTo == "vs_org")
            this.fetchSummary();
          this.processData();
        },
      );
    },

    /**
     * Required by teamTreeExpansionMixin - provides the API endpoint for fetching child team data
     * when expanding team rows in the breakdown table. Do not remove this method.
     * @returns {string} The URL endpoint for child data requests
     */
    getChildDataEndpoint() {
      return breakdownEndpoint;
    },

    /**
     * Required by teamTreeExpansionMixin - provides query parameters for fetching child team data
     * when expanding team rows. Do not remove this method.
     * @returns {Object} Parameters object containing filters and context for the API request
     */
    getChildDataParams() {
      return {
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
        atg: this.$store.snapshotResultFilters.aggregationTagGroup?.encoded_id,
        question_id: questionId,
        aggregation: this.$store.snapshotResultFilters.aggregation,
      };
    },

    /**
     * Required by teamTreeExpansionMixin - provides cached squad data for parent/child lookups
     * and team hierarchy navigation. Do not remove this method.
     * @returns {Array} Array of squad objects with id, parent_id, is_parent properties
     */
    getCachedSquads() {
      return [];
    },

    /**
     * Required by teamTreeExpansionMixin - provides benchmark data to apply to child team rows
     * when they are loaded. Do not remove this method.
     * @returns {Object} Benchmark data with p_50, p_75, p_90 properties
     */
    getBenchmarkData() {
      return {};
    },
    fetchAll() {
      // Called by peopleFiltersMixin when filters change
      this.fetchSummary();
    },
    fetchSummary() {
      const queryString = objectToQueryString({
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
        ct: this.compareTo,
        prev_ss: this.previousSnapshotId,
      });

      this.summaryUpdating = this.summaryLoaded;

      fetch(`${summaryEndpoint}?${queryString}`)
        .then((response) => response.json())
        .then((data) => {
          this.summaryData = data;
          this.processData();
          this.summaryLoaded = true;
          this.summaryUpdating = false;
        });
    },
    processData() {
      this.chartData = [];
      this.comparisonChartData = [];

      if (
        isCoreQuestion &&
        ["vs_50th", "vs_75th", "vs_90th"].includes(this.compareTo)
      ) {
        this.processCoreQuestionData();
      } else {
        this.summaryData.results.forEach((item, idx) => {
          const color =
            item.name === "Other"
              ? _SS_ALLOC_COLOR_OTHER
              : getChartColor(idx, this.summaryData.results.length);

          this.colors[item.id] = color;

          this.chartData.push(
            Object.assign({}, item, {
              name: this.questionItemLabel(item),
              tooltip: this.chartTooltip([item]),
            }),
          );
        });
      }

      this.generateComparisonChartData();
    },
    processCoreQuestionData() {
      const innovation = {
        id: "innovation",
        name: "New capabilities",
        pct: sum(this.bucketedItems.innovation, "pct"),
        tooltip: this.chartTooltip(this.bucketedItems.innovation),
      };

      const ktlo = {
        id: "ktlo",
        name: "KTLO and maintenance",
        pct: sum(this.bucketedItems.ktlo, "pct"),
        tooltip: this.chartTooltip(this.bucketedItems.ktlo),
      };

      const other = {
        id: "other",
        name: "Other",
        pct: sum(this.bucketedItems.other, "pct"),
        tooltip: this.chartTooltip(this.bucketedItems.other),
      };

      this.colors["innovation"] = getChartColor(0, 3);
      this.colors["ktlo"] = getChartColor(1, 3);
      this.colors["other"] = _SS_ALLOC_COLOR_OTHER;

      this.chartData.push(innovation);
      this.chartData.push(ktlo);
      this.chartData.push(other);
    },
    generateComparisonChartData() {
      if (this.compareTo == "vs_prev" || this.compareTo == "vs_org") {
        this.summaryData.comparison_results.forEach((item) => {
          this.comparisonChartData.push(
            Object.assign({}, item, { name: this.questionItemLabel(item) }),
          );
        });
      } else {
        if (!this.summaryData.industry_benchmarks) return;

        const { innovation, ktlo, other } = this.getIndustryBenchmarks();

        if (innovation)
          this.comparisonChartData.push({
            id: "innovation",
            pct: parseFloat(innovation).toFixed(1),
          });

        if (ktlo)
          this.comparisonChartData.push({
            id: "ktlo",
            pct: parseFloat(ktlo).toFixed(1),
          });

        if (other)
          this.comparisonChartData.push({
            id: "other",
            pct: parseFloat(other).toFixed(1),
          });
      }
    },
    getIndustryBenchmarks() {
      if (!isCoreQuestion) return {};

      let percentile = "p_50";
      if (this.compareTo == "vs_75th") percentile = "p_75";
      if (this.compareTo == "vs_90th") percentile = "p_90";

      const benchmarks = this.summaryData.industry_benchmarks;
      const innovation = benchmarks["allocation_self_report"]?.[percentile];
      const ktlo = benchmarks["allocation_ktlo_self_report"]?.[percentile];
      const other =
        100 - parseFloat(innovation).toFixed(1) - parseFloat(ktlo).toFixed(1);

      return {
        innovation,
        ktlo,
        other,
      };
    },
    fetchBreakdown() {
      this.breakdownUpdating = this.breakdownLoaded;

      const queryString = objectToQueryString({
        squad_ids: this.squadIds.join(","),
        group_ids: this.groupIds.join(","),
        tag_ids: this.tagIds.join(","),
        branch: this.$store.snapshotResultFilters.branch,
        atg: this.$store.snapshotResultFilters.aggregationTagGroup?.encoded_id,
        aggregation: this.$store.snapshotResultFilters.aggregation,
        parent_squad_id: this.currentParentSquadId,
      });

      fetch(`${breakdownEndpoint}?${queryString}`)
        .then((response) => response.json())
        .then((data) => {
          this.breakdownData = data.result_items;
          this.breakdownLoaded = true;
          this.breakdownUpdating = false;
        });
    },

    refetchTeamBreakdown() {
      this.resetTreeState();
      this.fetchBreakdown();
    },
    handleAggregationChange(detail) {
      if (detail.methodName && typeof this[detail.methodName] === "function") {
        this[detail.methodName]();
      }
    },
    openBreakdown() {
      this.determineSquadNamesFromSquadIds();
      this.breakdownOpen = true;
      setQueryStringParam("question", questionId);
      this.resetTreeState();
      this.fetchBreakdown();
    },
    closeBreakdown() {
      this.breakdownOpen = false;
      deleteQueryStringParam("question");
    },
    get breakdownItems() {
      // Check if we're in attribute aggregation mode
      const aggregation = this.$store?.snapshotResultFilters?.aggregation;
      if (aggregation?.startsWith("attribute_")) {
        // For attribute aggregation, return data directly without tree processing
        // Tag groups don't have hierarchical structure like teams
        if (!this.breakdownData || !Array.isArray(this.breakdownData)) {
          return [];
        }

        // Apply sorting for attribute data
        const sortedData = [...this.breakdownData].sort((a, b) => {
          const col = this.tableSort.col;
          const dir = this.tableSort.dir;

          let aVal, bVal;

          if (col === "group_name") {
            aVal = a.group_name || "";
            bVal = b.group_name || "";
          } else if (col === "sample_size") {
            aVal = a.sample_size || 0;
            bVal = b.sample_size || 0;
          } else {
            // Sorting by one of the allocation items
            aVal = parseFloat(a.items?.[col]) || 0;
            bVal = parseFloat(b.items?.[col]) || 0;
          }

          // Handle string vs number comparison
          if (typeof aVal === "string" && typeof bVal === "string") {
            return dir === "asc"
              ? aVal.localeCompare(bVal)
              : bVal.localeCompare(aVal);
          } else {
            return dir === "asc" ? aVal - bVal : bVal - aVal;
          }
        });

        return sortedData;
      }

      // For team aggregation, use the tree expansion mixin's processing
      return this.getBreakdownItems({
        directData: this.breakdownData,
      });
    },
    // Note: changeSquadIds and changeGroupIds methods are provided by peopleFiltersMixin

    changeTagIds(tagIds) {
      this.$store.snapshotResultFilters.tagIds = tagIds;
      setQueryStringParam("tag_ids", tagIds);

      this.fetchSummary();

      // Only clear tree state and refresh team breakdown if modal is actually open
      if (this.breakdownModalOpen) {
        this.resetTreeState();
        this.refetchTeamBreakdown();
      }
    },
    questionItemLabel(item) {
      return (questionItemOverrides[item.id] || item.name || "").trim();
    },
    get groupTitle() {
      // Check if we're in attribute mode
      const aggregation = this.$store?.snapshotResultFilters?.aggregation;
      if (aggregation?.startsWith("attribute_")) {
        return "Attribute";
      }

      return this.breakdownItems[0]?.group_title || "Team";
    },
    get squadIds() {
      const storeValue = this.$store?.snapshotResultFilters?.squadIds;
      const urlValue =
        getQueryStringParams()
          .get("squad_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    get groupIds() {
      const storeValue = this.$store?.snapshotResultFilters?.groupIds;
      const urlValue =
        getQueryStringParams()
          .get("group_ids")
          ?.split(",")
          .filter(Boolean)
          .map(Number) || [];
      return storeValue && storeValue.length > 0 ? [...storeValue] : urlValue;
    },
    get tagIds() {
      return this.$store.snapshotResultFilters.tagIds;
    },
    get compareTo() {
      return this.$store.snapshotResultFilters.compareTo;
    },
    get comparingToIndustry() {
      return ["vs_50th", "vs_75th", "vs_90th"].includes(this.compareTo);
    },
    get previousSnapshotId() {
      return this.$store.snapshotResultFilters.previousSnapshotId;
    },
    // This exists in all breakdowns to display the "Showing N teams and N attributes" context
    get tagNamesFromTagIds() {
      const selectedTags = tagOptions.filter(
        (tag) =>
          this.tagIds.includes(String(tag.id)) || this.tagIds.includes(tag.id),
      );

      return selectedTags.map((tag) => tag.name);
    },
    // This exists in all breakdowns to display the "Showing N teams and N attributes" context
    determineSquadNamesFromSquadIds() {
      const cachedSquads = snapshotSquadsFromCache(snapshotId);
      const selectedSquads = cachedSquads.filter(
        (squad) =>
          this.squadIds.includes(String(squad.id)) ||
          this.squadIds.includes(squad.id),
      );
      this.squadNamesFromSquadIds = selectedSquads.map((squad) => squad.name);
    },
    chartTooltip(items) {
      if (!items) return;

      const list = items.map((item) => {
        return `
          <div>
            <div class='flex items-center justify-between'>
              <span class='font-semibold max-w-64 tracking-tight'>${this.questionItemLabel(item)}</span>
              <span class='font-semibold'>${item.pct}%</span>
            </div>

            ${item.description?.length ? `<div class='text-gray-500 font-normal max-w-64 tracking-tight mt-0.5'>${item.description}</div>` : ""}
          </div>
        `;
      });

      return `<div class='w-80 p-2 text-tiny bg-white text-left'>
        <div class="uppercase mb-2.5 pb-px text-[11px] text-gray-400 font-semibold">
          Survey items
        </div>
        <div class='grid text-gray-700 text-tiny font-medium gap-y-2.5'>
          ${list.join("")}
        </div>
      </div>`;
    },
    tooltipIfTruncated(e, fullText) {
      const el = e.target;

      if (el && el.offsetWidth < el.scrollWidth) {
        tippy(el, {
          content: fullText,
          delay: [250, 0],
        });
      }
    },
    sortableHeader(column) {
      const isCurrentSort = column === this.tableSort.col;
      const isAsc = this.tableSort.dir === "asc";

      return `
        <div class="text-gray-300 flex flex-col -space-y-1.5 text-xs -mb-px">
          <i class="-mb-px bx bx-caret-up" :class="{'text-gray-900': ${
            isCurrentSort && isAsc
          }}"></i>
          <i class="bx bx-caret-down" :class="{'text-gray-900': ${
            isCurrentSort && !isAsc
          }}"></i>
        </div>
      `;
    },
    isActiveSort(column) {
      return this.tableSort.col === column;
    },
    changeTableSort(column, defaultDir = "desc") {
      if (column !== this.tableSort.col) {
        this.tableSort.col = column;
        this.tableSort.dir = defaultDir;
      } else {
        this.tableSort.dir = this.tableSort.dir === "asc" ? "desc" : "asc";
      }
    },
    get bucketedItems() {
      // Accounts can customize the core question and add multiple items that should be bucketed as innovation or ktlo or other.

      const innovation = this.summaryData.results.filter(
        (item) => item.bucket === "innovation",
      );
      const ktlo = this.summaryData.results.filter(
        (item) => item.bucket === "ktlo",
      );
      const other = this.summaryData.results.filter(
        (item) => item.bucket === null,
      );

      return { innovation, ktlo, other };
    },
    generateCsv() {
      const viewingByTeam = Object.keys(this.breakdownItems[0]).includes(
        "dri_name",
      );

      const benchmarkLabel = {
        vs_50th: "Industry P50",
        vs_75th: "Industry P75",
        vs_90th: "Industry P90",
      }[this.compareTo];

      // Create headers for the CSV
      const headers = [
        this.groupTitle,
        ...(viewingByTeam
          ? ["Parent team(s)", "Team lead", "Team lead email"]
          : []),
        ...this.summaryData.results.map((item) => this.questionItemLabel(item)),
        ...(isCoreQuestion && benchmarkLabel
          ? [
              `Innovation vs ${benchmarkLabel}`,
              `KTLO vs ${benchmarkLabel}`,
              `Other vs ${benchmarkLabel}`,
            ]
          : []),
      ];

      const b = this.getIndustryBenchmarks();

      // Create rows for the CSV
      const rows = this.breakdownItems.map((data) => {
        const hidden = Object.values(data.items).some(
          (item) => item === "hidden",
        );

        const innovation = sum(
          this.bucketedItems.innovation.map((i) => data.items[i.id]),
        );
        const ktlo = sum(this.bucketedItems.ktlo.map((i) => data.items[i.id]));
        const other = sum(
          this.bucketedItems.other.map((i) => data.items[i.id]),
        );

        const row = [
          data.group_name,
          ...(viewingByTeam
            ? [
                data.group_parents || "",
                data.dri_name || "",
                data.dri_email || "",
              ]
            : []),
          ...this.summaryData.results.map((item) => {
            const value = data.items[item.id];
            return hidden ? "" : value;
          }),
          ...(isCoreQuestion && benchmarkLabel
            ? [
                hidden ? "" : (innovation - b.innovation).toFixed(1),
                hidden ? "" : (ktlo - b.ktlo).toFixed(1),
                hidden ? "" : (other - b.other).toFixed(1),
              ]
            : []),
        ];
        return row;
      });

      // Combine headers and rows with proper escaping for both
      const csvContent = [
        headers.map(escapeCsvValue).join(","),
        ...rows.map((row) => row.map(escapeCsvValue).join(",")),
      ].join("\n");

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `allocation_breakdown_${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
  };
}
